import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Fuse from 'fuse.js';

import { db } from '../../../middleware/firebase';
import { priceIva, getRegularPrice } from '../../../middleware/iva';
import { formatNumber } from '../../../middleware/common-functions';
import { AddProductModal } from '../../common/modals/modals';
import { addProductCart } from '../../../middleware/cartFunctions';
import { ListBlock } from '../../common/products/productBlock';
import Filters from '../products/filters';
import Loader from '../../common/loader';
import axios from '../../../middleware/axios';

const FUSE_OPTIONS = {
  keys: [
    { name: 'title', weight: 0.6 },
    { name: 'keywords', weight: 0.3 },
    { name: 'aliasd', weight: 0.1 },
  ],
  threshold: 0.4,
  includeScore: true,
  minMatchCharLength: 2,
};

// Multi-word search: run Fuse separately per word, score each product by how
// many words matched, then sort by match count desc → average score asc.
const fuseSearch = (fuse, query) => {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return fuse.search(query);

  // Map id → { item, matchCount, scoreSum }
  const scoreMap = {};
  words.forEach((word) => {
    fuse.search(word).forEach(({ item, score }) => {
      if (!scoreMap[item.id]) scoreMap[item.id] = { item, matchCount: 0, scoreSum: 0 };
      scoreMap[item.id].matchCount += 1;
      scoreMap[item.id].scoreSum += (score || 0);
    });
  });

  return Object.values(scoreMap)
    .sort((a, b) =>
      b.matchCount - a.matchCount || a.scoreSum - b.scoreSum
    )
    .map(({ item, scoreSum, matchCount }) => ({ item, score: scoreSum / matchCount }));
};

const BATCH_SIZE = 10;

const fetchProductsByIds = async (ids) => {
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    batches.push(ids.slice(i, i + BATCH_SIZE));
  }
  const results = await Promise.all(
    batches.map((batch) =>
      db.collection('products').where('id', 'in', batch).get()
    )
  );
  const productMap = {};
  results.forEach((snapshot) => {
    snapshot.docs.forEach((doc) => {
      const product = doc.data();
      product.price_num = priceIva(product.zonePrice, product.price);
      product.price = formatNumber(product.price_num);
      product.price_regular = getRegularPrice(product.zonePrice, product.price_num);
      productMap[product.id] = product;
    });
  });
  return ids.map((id) => productMap[id]).filter(Boolean);
};

// Derive price range buckets from a list of numeric prices
const getPriceRanges = (prices) => {
  const nums = prices.map((p) => parseFloat(p)).filter((n) => !isNaN(n) && n > 0);
  if (nums.length < 5) return [];
  const sorted = [...nums].sort((a, b) => a - b);
  const len = sorted.length;
  const at = (q) => sorted[Math.min(Math.floor(len * q), len - 1)];
  const round = (n) => Math.round(n / 50) * 50;
  const quantiles = [0.2, 0.35, 0.5, 0.65, 0.8];
  return quantiles.map((q, i) => {
    const lo = round(at(i === 0 ? 0 : quantiles[i - 1]));
    const hi = round(at(q));
    if (i === 0) return `Menor a $${hi}`;
    if (i === quantiles.length - 1) return `Mayor a $${lo}`;
    return `$${lo} - $${hi - 1}`;
  });
};

const priceInRange = (price_num, rangeStr) => {
  const nums = (rangeStr.match(/\d+/g) || []).map(Number);
  if (rangeStr.includes('Menor')) return price_num < nums[0];
  if (rangeStr.includes('Mayor')) return price_num > nums[0];
  return price_num >= nums[0] && price_num <= nums[1];
};

const buildFilterData = (products) => {
  const families = [], types = [], brands = [], sizes = [], others = [], prices = [];
  products.forEach((p) => {
    if (p.grpsbi) {
      if (p.grpsbi[1]) families.push(p.grpsbi[1]);
      if (p.grpsbi[2]) types.push(p.grpsbi[2]);
    }
    if (p.aliasd) brands.push(p.aliasd);
    if (p.grpsbiObj) {
      if (p.grpsbiObj['3']) sizes.push(p.grpsbiObj['3']);
      if (p.grpsbiObj['4']) others.push(p.grpsbiObj['4']);
    }
    if (p.price_num) prices.push(p.price_num);
  });
  const unique = (arr) => [...new Set(arr)].filter(Boolean).sort();
  return {
    family:  { title: 'Categoría',        type: 'checkbox', elements: unique(families), selected: [] },
    type:    { title: 'Tipo',             type: 'checkbox', elements: unique(types),    selected: [] },
    brands:  { title: 'Marca',            type: 'checkbox', elements: unique(brands),   selected: [] },
    size:    { title: 'Tamaño',           type: 'checkbox', elements: unique(sizes),    selected: [] },
    others:  { title: 'Atributo/Calidad', type: 'checkbox', elements: unique(others),   selected: [] },
    prices:  { title: 'Precio',           type: 'checkbox', elements: getPriceRanges(prices), selected: [] },
  };
};

const applyFilters = (products, filterData) => {
  return products.filter((p) => {
    if (filterData.family.selected.length && !filterData.family.selected.some((v) => p.grpsbi && p.grpsbi.includes(v))) return false;
    if (filterData.type.selected.length   && !filterData.type.selected.some((v)   => p.grpsbi && p.grpsbi.includes(v))) return false;
    if (filterData.brands.selected.length && !filterData.brands.selected.includes(p.aliasd)) return false;
    if (filterData.size.selected.length   && !filterData.size.selected.includes(p.grpsbiObj && p.grpsbiObj['3'])) return false;
    if (filterData.others.selected.length && !filterData.others.selected.includes(p.grpsbiObj && p.grpsbiObj['4'])) return false;
    if (filterData.prices.selected.length && !filterData.prices.selected.some((r) => priceInRange(p.price_num, r))) return false;
    return true;
  });
};

const emptyFilterData = () => ({
  family:  { title: 'Categoría',        type: 'checkbox', elements: [], selected: [] },
  type:    { title: 'Tipo',             type: 'checkbox', elements: [], selected: [] },
  brands:  { title: 'Marca',            type: 'checkbox', elements: [], selected: [] },
  size:    { title: 'Tamaño',           type: 'checkbox', elements: [], selected: [] },
  others:  { title: 'Atributo/Calidad', type: 'checkbox', elements: [], selected: [] },
  prices:  { title: 'Precio',           type: 'checkbox', elements: [], selected: [] },
});

// Encode active filter selections into URL search params (alongside q=)
const filtersToParams = (q, filterData) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  const FILTER_KEYS = ['family', 'type', 'brands', 'size', 'others', 'prices'];
  FILTER_KEYS.forEach((key) => {
    filterData[key].selected.forEach((val) => params.append(key, val));
  });
  return params.toString();
};

// Read filter selections back out of URL params into filterData
const applyParamsToFilterData = (params, filterData) => {
  const next = JSON.parse(JSON.stringify(filterData)); // deep clone
  ['family', 'type', 'brands', 'size', 'others', 'prices'].forEach((key) => {
    const vals = params.getAll(key);
    if (vals.length) next[key].selected = vals;
  });
  return next;
};

const SearchPage = () => {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const query = params.get('q') || '';

  const [allProducts, setAllProducts] = useState(null);   // full unfiltered set
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState(emptyFilterData());
  const [modal, setModal] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);
  const [mobFilter, setMobFilter] = useState(false);

  const catalogRef = useRef(null);
  const catalogLoadingRef = useRef(null);

  const loadCatalog = () => {
    if (catalogRef.current) return Promise.resolve(catalogRef.current);
    if (catalogLoadingRef.current) return catalogLoadingRef.current;
    catalogLoadingRef.current = axios
      .get('scriptsProductsCatalog')
      .then((res) => { catalogRef.current = res.data; return res.data; })
      .catch(() => { catalogLoadingRef.current = null; return []; });
    return catalogLoadingRef.current;
  };

  const [synonymLinks, setSynonymLinks] = useState([]);

  const expandWithSynonyms = async (q) => {
    const normalized = q.toLowerCase().trim();
    const snap = await db.collection('search-synonyms').where('term', '==', normalized).limit(1).get();
    if (snap.empty) { setSynonymLinks([]); return [q]; }
    const doc = snap.docs[0].data();
    setSynonymLinks(doc.links || []);
    return [q, ...(doc.aliases || [])];
  };

  const logLowResultQuery = async (q, count) => {
    if (count > 5) return;
    const ref = db.collection('search-queries').doc(encodeURIComponent(q.toLowerCase().trim()));
    try {
      const snap = await ref.get();
      if (snap.exists) {
        const data = snap.data();
        // Only re-open ignored/done entries if results are still low
        const updates = { count: (data.count || 1) + 1, lastSeen: new Date(), results: count };
        if (data.status === 'done' || data.status === 'ignore') {
          // Keep existing status — admin already reviewed it
        } else {
          updates.status = 'pending';
        }
        await ref.update(updates);
      } else {
        await ref.set({ query: q.toLowerCase().trim(), results: count, status: 'pending', count: 1, lastSeen: new Date(), createdAt: new Date() });
      }
    } catch (_) { /* non-critical, swallow */ }
  };

  useEffect(() => {
    if (!query) { setAllProducts([]); setFilterData(emptyFilterData()); setSynonymLinks([]); return; }
    setLoading(true);
    setAllProducts(null);
    setFilterData(emptyFilterData());
    setSynonymLinks([]);

    Promise.all([loadCatalog(), expandWithSynonyms(query)]).then(async ([catalog, terms]) => {
      if (!catalog || !catalog.length) { setAllProducts([]); setLoading(false); return; }

      // Exact idpart match → redirect straight to product page
      const idpartMatch = catalog.find(
        (p) => p.idpart && p.idpart.toLowerCase() === query.toLowerCase().trim()
      );
      if (idpartMatch) {
        history.replace(`/product/${idpartMatch.id}`);
        return;
      }

      const fuse = new Fuse(catalog, FUSE_OPTIONS);
      const seen = new Set();
      const rankedIds = terms.flatMap((t) =>
        fuseSearch(fuse, t).map((r) => r.item.id)
      ).filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }).slice(0, 60);

      if (!rankedIds.length) {
        setAllProducts([]);
        setLoading(false);
        logLowResultQuery(query, 0);
        return;
      }

      const full = await fetchProductsByIds(rankedIds);
      const withPrice = full
        .filter((p) => p.price && p.price !== '0.00')
        .sort((a, b) => b.price_num - a.price_num);
      setAllProducts(withPrice);
      // Build filter options from results, then restore any selections from URL
      const builtFilters = buildFilterData(withPrice);
      const currentParams = new URLSearchParams(window.location.search);
      setFilterData(applyParamsToFilterData(currentParams, builtFilters));
      setLoading(false);
      logLowResultQuery(query, withPrice.length);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const products = useMemo(
    () => (allProducts ? applyFilters(allProducts, filterData) : null),
    [allProducts, filterData]
  );

  const updateFilters = (key) => (value, type) => {
    setFilterData((prev) => {
      const next = { ...prev, [key]: { ...prev[key], selected: [...prev[key].selected] } };
      const sel = next[key].selected;
      if (type === 'price') {
        next[key].selected = sel.includes(value) ? [] : [value];
      } else {
        next[key].selected = sel.includes(value)
          ? sel.filter((v) => v !== value)
          : [...sel, value];
      }
      // Sync to URL (replace so back-button skips every filter tick)
      history.replace({ search: filtersToParams(query, next) });
      return next;
    });
  };

  const promptAddProduct = async (ev) => {
    const product_id = ev.currentTarget.dataset.product_id;
    const product = allProducts.find((p) => p.id === product_id);
    const product_amount =
      document.getElementById(`cant_input_list_${product_id}`)?.value || 1;
    product.amount = product_amount;
    setAddedProduct(product);
    await addProductCart({ product_amount, product_id, product_data: product });
    setModal(true);
  };

  const hideModal = () => setModal(false);

  const hasActiveFilters = Object.values(filterData).some((f) => f.selected.length > 0);

  return (
    <div className="container-fluid first-container pt-4">
      <Helmet>
        <title>Búsqueda: {query} — Mathasa</title>
      </Helmet>

      <div className="search_page_header">
        <h4 className="search_page_title">
          {query ? <>Resultados para: <span>"{query}"</span></> : 'Búsqueda'}
        </h4>
        {products && !loading && (
          <span className="search_page_count">
            {products.length} {products.length === 1 ? 'producto' : 'productos'}
            {hasActiveFilters && allProducts && ` de ${allProducts.length}`}
          </span>
        )}
      </div>

      {synonymLinks.length > 0 && (
        <div className="search_page_links">
          {synonymLinks.map((lnk, i) => (
            <a key={i} href={lnk.url} className="search_page_link_chip">
              <i className="fas fa-arrow-right mr-2"></i>{lnk.label}
            </a>
          ))}
        </div>
      )}

      {loading && <Loader />}

      {!loading && allProducts && (
        <div className="d-md-flex">
          {/* Mobile filter toggle */}
          <div className="d-md-none mt-2 mb-3">
            <button
              onClick={() => setMobFilter(!mobFilter)}
              className="btn btn-block material-shadows py-3"
            >
              <i className="fas fa-filter mr-2"></i> Filtros
            </button>
            {mobFilter && (
              <Filters filters={filterData} updateFilters={updateFilters} />
            )}
          </div>

          {/* Desktop filters sidebar */}
          <div className="d-none d-md-inline search_page_filters">
            <h5 className="underlined my-4">Filtros</h5>
            <Filters filters={filterData} updateFilters={updateFilters} />
          </div>

          {/* Results */}
          <div className="products_list">
            {products && products.length > 0 ? (
              <div className="product_block_container">
                {products.map((product) => (
                  <ListBlock
                    key={product.id}
                    product={product}
                    addProductCart={promptAddProduct}
                    from="list"
                  />
                ))}
              </div>
            ) : (
              <div className="search_page_empty">
                <i className="fas fa-search"></i>
                <p>
                  {hasActiveFilters
                    ? 'Sin resultados con los filtros seleccionados.'
                    : <>Sin resultados para <strong>"{query}"</strong></>}
                </p>
                {hasActiveFilters && (
                  <button
                    className="btn btn-link p-0"
                    onClick={() => {
                      const clean = buildFilterData(allProducts);
                      setFilterData(clean);
                      history.replace({ search: `q=${encodeURIComponent(query)}` });
                    }}
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {addedProduct && (
        <AddProductModal
          active={modal}
          toggle={hideModal}
          product_title={addedProduct.descp1}
          type={addedProduct.xium ? addedProduct.xium.descrp : 'pz'}
          neddedProducts_ids={addedProduct.ptrl}
          relatedTypes={addedProduct.grpsbi ? addedProduct.grpsbi[2] : null}
          product_amount={addedProduct.amount}
        />
      )}
    </div>
  );
};

export default SearchPage;
